import React, { useCallback, useMemo } from 'react';
import { Word, WordType } from '@goldcast/player-config/src/types/transcript-types';
import { HighlightedSearchWord } from './types';
import PartialHighlightedWord from './PartialHighlightedWord';
import DefaultWord from './DefaultWord';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { classnames } from '@/libs/utils';
import classes from './Transcript.module.scss';

export default function TranscriptWord({
  word,
  foundWords,
  forceHighlight = false,
  isCurrentPlaybackWord = false,
  isEdit = false,
  isFullClip,
  isClipFromRS,
  isWordDeleted,
  segmentId
}: {
  word: Word;
  foundWords: { [key: number]: HighlightedSearchWord };
  forceHighlight?: boolean;
  isCurrentPlaybackWord?: boolean;
  isEdit?: boolean;
  isFullClip: boolean;
  isClipFromRS: boolean;
  isWordDeleted: boolean;
  segmentId?: string;
}) {
  const wordCase = useMemo(() => {
    if (foundWords[word.start_time]) {
      return 'HIGHLIGHTED';
    }
    return 'DEFAULT';
  }, [word, foundWords]);

  const isWordVisible = useCallback((word: Word) => {
    return word.type !== WordType.Punctuation;
  }, []);

  const fillerWordClassName = useMemo(() => {
    const canUseFillerWords = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.USE_CL_FILLER_WORDS];
    return canUseFillerWords && word.is_filler && !isWordDeleted
      ? 'underline decoration-dotted underline-offset-2'
      : '';
  }, [word.is_filler, isWordDeleted, isFullClip, isClipFromRS]);

  const getTextColor = useMemo(() => {
    if (isWordDeleted || word.hidden) {
      return 'text-slate-400';
    }
    return '';
  }, [isWordDeleted, word.hidden]);

  return (
    isWordVisible(word) && (
      <div
        data-testid="transcript-word"
        data-word-id={word.index}
        data-word-start-time={word.start_time}
        data-word-end-time={word.end_time}
        data-segment-id={segmentId}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={classnames(
          classes.word,
          'word',
          'relative',
          '-mx-px',
          'inline-flex',
          'select-none',
          'items-center',
          {
            'line-through': isWordDeleted,
            'transcript-hover cursor-default': isEdit && !forceHighlight,
            'transcript-selection text-black': forceHighlight && !isWordDeleted,
            '!bg-slate-300 !text-slate-500': isWordDeleted && forceHighlight,
            [classes['active-word']]: isCurrentPlaybackWord
          },
          getTextColor,
          fillerWordClassName
        )}
      >
        <span className="relative z-10 cursor-pointer px-[3px] py-[3.5px]">
          {wordCase === 'HIGHLIGHTED' ? (
            <PartialHighlightedWord partialHighlightWord={foundWords[word.start_time]} content={word.content} />
          ) : (
            <DefaultWord content={word.content + ' '} />
          )}
        </span>
      </div>
    )
  );
}
